import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, Tooltip, Legend } from 'chart.js';
import { Bar, Bubble } from 'react-chartjs-2';
import { getTokenSplit, getCountSplit, getAvgDurationSplit } from '../services/ObservabilityService';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, Tooltip, Legend);

const Page = styled.div`
  padding: 16px 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 20px;
  margin: 0;
`;

const Subtle = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin: 12px 0 16px 0;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
`;

const CardTitle = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const CardValue = styled.div`
  font-weight: 700;
  font-size: 1.6rem;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const ChartCard = styled(Card)`
  height: 430px;
`;

const FullWidthChart = styled(Card)`
  height: 500px;
`;

const Toolbar = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DatePickerContainer = styled.div`
  position: relative;
`;

const Button = styled.button`
  padding: 6px 10px;
  border: 1px solid #0ea5e9;
  color: #0ea5e9;
  border-radius: 6px;
  background: #ffffff;
  cursor: pointer;
`;

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function formatThousands(n) {
  return toNum(n).toLocaleString();
}

export default function ObservabilityEntityDetails() {
  const { metricType, metricCode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tokenSplit, setTokenSplit] = useState([]);
  const [countSplit, setCountSplit] = useState([]);
  const [durationSplit, setDurationSplit] = useState([]);
  const [showPicker, setShowPicker] = useState(false);

  const startMs = Number(searchParams.get('start')) || null;
  const endMs = Number(searchParams.get('end')) || null;

  const dateRange = useMemo(() => ({
    startDate: startMs ? new Date(startMs) : new Date(Date.now() - 24 * 60 * 60 * 1000),
    endDate: endMs ? new Date(endMs) : new Date(),
    key: 'selection'
  }), [startMs, endMs]);

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      setLoading(true);
      setError(null);
      try {
        const body = { started_at: { $gte: dateRange.startDate.getTime(), $lte: dateRange.endDate.getTime() } };
        const [t, c, d] = await Promise.all([
          getTokenSplit(metricType, metricCode, body),
          getCountSplit(metricType, metricCode, body),
          getAvgDurationSplit(metricType, metricCode, body)
        ]);
        if (cancelled) return;
        setTokenSplit(Array.isArray(t) ? t : (t?.data || []));
        setCountSplit(Array.isArray(c) ? c : (c?.data || []));
        setDurationSplit(Array.isArray(d) ? d : (d?.data || []));
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, [metricType, metricCode, dateRange.startDate, dateRange.endDate]);

  const joined = useMemo(() => {
    const map = new Map();
    (tokenSplit || []).forEach((it) => {
      const code = it.metric_type_code;
      map.set(code, {
        code,
        name: it.metric_type_name,
        input_tokens: toNum(it.input_tokens),
        output_tokens: toNum(it.output_tokens),
        total_tokens: toNum(it.total_tokens),
      });
    });
    (countSplit || []).forEach((it) => {
      const code = it.metric_type_code;
      const row = map.get(code) || { code, name: it.metric_type_name };
      row.count = toNum(it.count);
      map.set(code, row);
    });
    (durationSplit || []).forEach((it) => {
      const code = it.metric_type_code;
      const row = map.get(code) || { code, name: it.metric_type_name };
      row.avg_duration_ms = toNum(it.avg_duration_ms);
      map.set(code, row);
    });
    return Array.from(map.values());
  }, [tokenSplit, countSplit, durationSplit]);

  const totals = useMemo(() => {
    const tokens = joined.reduce((s, x) => s + toNum(x.total_tokens), 0);
    const execs = joined.reduce((s, x) => s + toNum(x.count), 0);
    const durations = joined.map((x) => toNum(x.avg_duration_ms)).filter((n) => n > 0);
    const avg = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0;
    return { tokens, execs, avg };
  }, [joined]);

  const tokenLabels = useMemo(() => joined.map((x) => x.name || x.code), [joined]);

  const tokenBarData = useMemo(() => ({
    labels: tokenLabels,
    datasets: [
      { label: 'Input Tokens', data: joined.map((x) => x.input_tokens || 0), backgroundColor: '#3b82f6' },
      { label: 'Output Tokens', data: joined.map((x) => x.output_tokens || 0), backgroundColor: '#f97316' },
    ]
  }), [tokenLabels, joined]);

  const countBarData = useMemo(() => ({
    labels: tokenLabels,
    datasets: [
      { label: 'Execution Count', data: joined.map((x) => x.count || 0), backgroundColor: '#10b981' }
    ]
  }), [tokenLabels, joined]);

  const durationBarData = useMemo(() => {
    const arr = [...joined].sort((a, b) => (b.avg_duration_ms || 0) - (a.avg_duration_ms || 0));
    return {
      labels: arr.map((x) => x.name || x.code),
      datasets: [
        { label: 'Avg Duration (ms)', data: arr.map((x) => x.avg_duration_ms || 0), backgroundColor: '#ef4444' }
      ]
    };
  }, [joined]);

  const bubbleData = useMemo(() => {
    const counts = joined.map((x) => x.count || 0);
    const minC = Math.min(...counts, 0);
    const maxC = Math.max(...counts, 1);
    const scaleR = (c) => {
      const t = (c - minC) / (maxC - minC || 1);
      return 6 + t * 18;
    };
    return {
      datasets: [
        {
          label: 'Pipeline Components',
          data: joined.map((x) => ({ x: x.avg_duration_ms || 0, y: x.total_tokens || 0, r: scaleR(x.count || 0) })),
          backgroundColor: 'rgba(99,102,241,0.7)'
        }
      ]
    };
  }, [joined]);

  const durationBarOptions = useMemo(() => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    scales: { x: { ticks: { callback: (v) => `${Math.round(v / 1000)}s` } } }
  }), []);

  const barOptions = useMemo(() => ({ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true } } }), []);
  const bubbleOptions = useMemo(() => ({ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true } } }), []);

  return (
    <Page>
      <Header>
        <div>
          <Title>Entity Analytics · {metricType} · {metricCode}</Title>
          <Subtle>
            Range: {new Date(dateRange.startDate).toLocaleString()} → {new Date(dateRange.endDate).toLocaleString()}
          </Subtle>
        </div>
        <Toolbar>
          <DatePickerContainer>
            <Button onClick={() => setShowPicker((s) => !s)}>Select dates</Button>
            {showPicker && (
              <div style={{ position: 'absolute', right: 0, zIndex: 10 }}>
                <DateRangePicker
                  ranges={[dateRange]}
                  onChange={(r) => {
                    const s = r.selection.startDate.getTime();
                    const e = r.selection.endDate.getTime();
                    const params = new URLSearchParams(searchParams);
                    params.set('start', String(s));
                    params.set('end', String(e));
                    setSearchParams(params);
                    setShowPicker(false);
                  }}
                />
              </div>
            )}
          </DatePickerContainer>
        </Toolbar>
      </Header>

      <KPIGrid>
        <Card>
          <CardTitle>Total Tokens</CardTitle>
          <CardValue>{formatThousands(totals.tokens)}</CardValue>
        </Card>
        <Card>
          <CardTitle>Total Executions</CardTitle>
          <CardValue>{formatThousands(totals.execs)}</CardValue>
        </Card>
        <Card>
          <CardTitle>Avg Latency</CardTitle>
          <CardValue>{formatThousands(totals.avg)} ms</CardValue>
        </Card>
      </KPIGrid>

      {error && <div style={{ color: 'crimson', marginBottom: 8 }}>{String(error)}</div>}
      {loading && <div style={{ color: 'var(--text-secondary)', marginBottom: 8 }}>Loading…</div>}

      <ChartsGrid>
        <ChartCard>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Token Consumption Breakdown</div>
          <div style={{ width: '100%', height: '370px' }}>
            <Bar data={tokenBarData} options={barOptions} />
          </div>
        </ChartCard>
        <ChartCard>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Execution Count</div>
          <div style={{ width: '100%', height: '370px' }}>
            <Bar data={countBarData} options={barOptions} />
          </div>
        </ChartCard>
        <FullWidthChart style={{ gridColumn: 'span 2 / span 2' }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Average Duration Analysis</div>
          <div style={{ width: '100%', height: '430px' }}>
            <Bar data={durationBarData} options={durationBarOptions} />
          </div>
        </FullWidthChart>
        <FullWidthChart style={{ gridColumn: 'span 2 / span 2' }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Efficiency Bubble Scatter</div>
          <div style={{ width: '100%', height: '430px' }}>
            <Bubble data={bubbleData} options={bubbleOptions} />
          </div>
        </FullWidthChart>
      </ChartsGrid>
    </Page>
  );
}
